import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { 
  ArrowLeft, Book, FileText, Video, ExternalLink, 
  Search, RefreshCw, Star, Download, HelpCircle, 
  Scale, GraduationCap, FolderOpen, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const categoryIcons = {
  guides: Book,
  forms: FileText,
  training: GraduationCap,
  legal: Scale,
  faq: HelpCircle,
  all: FolderOpen
};

const categoryLabels = {
  guides: 'Guides',
  forms: 'Forms',
  training: 'Training',
  legal: 'Legal',
  faq: 'FAQ'
};

export default function ResourceCenter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchResources();
  }, [selectedCategory]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await api.get(`/resources?${params.toString()}`);
      setResources(response.data.resources);
      // Convert grouped to categories counts
      const grouped = response.data.grouped || {};
      const catCounts = {};
      for (const [cat, items] of Object.entries(grouped)) {
        catCounts[cat] = items.length;
      }
      setCategories(catCounts);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchResources();
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return Video;
      case 'pdf': return FileText;
      case 'link': return ExternalLink;
      default: return FileText;
    }
  };

  const allCategories = ['all', ...Object.keys(categoryLabels)];

  if (loading && resources.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']" data-testid="resource-center-title">Resource Center</h1>
              <p className="text-xs sm:text-sm text-slate-500">Forms, guides, and training materials</p>
            </div>
          </div>
          <form onSubmit={handleSearch} className="flex gap-2 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full sm:w-64"
                data-testid="resource-search-input"
              />
            </div>
            <Button type="submit" variant="secondary" size="sm" data-testid="resource-search-btn">
              Search
            </Button>
          </form>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {allCategories.map((cat) => {
            const Icon = categoryIcons[cat] || FolderOpen;
            const count = cat === 'all' ? resources.length : (categories[cat] || 0);
            return (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
                className={selectedCategory === cat ? 'bg-teal-500 hover:bg-teal-600' : ''}
                data-testid={`category-${cat}`}
              >
                <Icon className="w-4 h-4" />
                {cat === 'all' ? 'All' : categoryLabels[cat]}
                <Badge variant="secondary" className="ml-2 bg-slate-200 text-slate-700">
                  {count}
                </Badge>
              </Button>
            );
          })}
        </div>

        {/* Featured Resources */}
        {selectedCategory === 'all' && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Featured Resources
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {resources.filter(r => r.is_featured).map((resource) => {
                const TypeIcon = getTypeIcon(resource.type);
                const CatIcon = categoryIcons[resource.category] || Book;
                const handleClick = () => {
                  if (resource.url?.startsWith('/')) {
                    navigate(resource.url);
                  } else if (resource.url) {
                    window.open(resource.url, '_blank');
                  }
                };
                return (
                  <Card 
                    key={resource.resource_id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer border-teal-200 bg-teal-50/30" 
                    data-testid={`featured-resource-${resource.resource_id}`}
                    onClick={handleClick}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                          <CatIcon className="w-5 h-5 text-teal-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-slate-900 mb-1 truncate">{resource.title}</h3>
                          <p className="text-sm text-slate-500 line-clamp-2">{resource.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              <TypeIcon className="w-3 h-3 mr-1" />
                              {resource.type}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {categoryLabels[resource.category] || resource.category}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* All Resources List */}
        <div>
          <h2 className="text-lg font-semibold mb-4">
            {selectedCategory === 'all' ? 'All Resources' : categoryLabels[selectedCategory]}
          </h2>
          
          {resources.length > 0 ? (
            <div className="space-y-3">
              {resources.filter(r => selectedCategory === 'all' || !r.is_featured).map((resource) => {
                const TypeIcon = getTypeIcon(resource.type);
                const CatIcon = categoryIcons[resource.category] || Book;
                return (
                  <Card key={resource.resource_id} className="hover:shadow-md transition-shadow" data-testid={`resource-${resource.resource_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <CatIcon className="w-6 h-6 text-slate-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-slate-900 mb-1">{resource.title}</h3>
                            <p className="text-sm text-slate-500 line-clamp-1">{resource.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">
                                <TypeIcon className="w-3 h-3 mr-1" />
                                {resource.type}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">
                                {categoryLabels[resource.category] || resource.category}
                              </Badge>
                              {resource.tags?.slice(0, 2).map((tag, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs text-slate-400">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {resource.type === 'pdf' && (
                            <Button size="sm" variant="outline">
                              <Download className="w-4 h-4" />
                              Download
                            </Button>
                          )}
                          {resource.type === 'video' && (
                            <Button size="sm" variant="outline" asChild>
                              <a href={resource.url} target="_blank" rel="noopener noreferrer">
                                <Video className="w-4 h-4" />
                                Watch
                              </a>
                            </Button>
                          )}
                          {resource.type === 'link' && resource.url?.startsWith('/') ? (
                            <Button size="sm" variant="outline" onClick={() => navigate(resource.url)} data-testid={`view-${resource.resource_id}`}>
                              <FileText className="w-4 h-4" />
                              View
                            </Button>
                          ) : resource.type === 'link' && (
                            <Button size="sm" variant="outline" asChild>
                              <a href={resource.url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4" />
                                Open
                              </a>
                            </Button>
                          )}
                          {resource.type === 'document' && resource.url?.startsWith('/') ? (
                            <Button size="sm" variant="outline" onClick={() => navigate(resource.url)} data-testid={`view-${resource.resource_id}`}>
                              <FileText className="w-4 h-4" />
                              View Guide
                            </Button>
                          ) : resource.type === 'document' && (
                            <Button size="sm" variant="outline">
                              <FileText className="w-4 h-4" />
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Search className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium mb-2">No Resources Found</h3>
                <p className="text-slate-500">Try adjusting your search or filter criteria</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
